import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import useStylesheets from '@vromo/hooks/useStylesheets';

import styled from '@emotion/styled';
import { combine, featureCollection, point } from '@vromo/geojson';
import { observer } from 'mobx-react-lite';
import bbox from '@turf/bbox';
import some from 'lodash/some';
import MapboxStyle from 'mapbox-gl/dist/mapbox-gl.css';

import { isAllowedByOptions } from '@vromo/utils/server';
import { mapboxStyles, secrets } from '@vromo/api';

const DEFAULT_SOURCE = {
    type: 'geojson',
    data: {
        type: 'FeatureCollection',
        features: []
    }
};

const Container = styled.div`
    flex: 1;
    background-color: white;
    position: relative;
    width: 100%;
    height: 200px;
`;

interface Props {
    points: [number, number][];
}

export default observer<Props>(function JobsDistributionMap({ points }) {
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    useStylesheets(MapboxStyle);

    function fitBounds() {
        const map = mapRef.current;
        const bounds = bbox(combine(featureCollection(map?.getSource('jobs')._data.features)));

        if (!some(bounds, (it) => it === Infinity)) {
            map?.fitBounds(bounds, {
                linear: false,
                padding: 40,
                maxZoom: 16
            });
        }
    }

    const supported = isAllowedByOptions() && mapboxgl.supported();

    useEffect(() => {
        if (!supported) {
            return;
        }

        mapboxgl.accessToken = secrets.mapbox;


        const map = new mapboxgl.Map({
            container: mapContainerRef.current!,
            style: mapboxStyles.light,
            refreshExpiredTiles: false,
            zoom: 1,
            center: [1, 1],
            interactive: true
        });

        mapRef.current = map;

        map.on('load', () => {
            map.addSource('jobs', DEFAULT_SOURCE);

            map.addLayer(
                {
                    id: 'jobs-layer',
                    type: 'heatmap',
                    source: 'jobs',
                    paint: {
                        // Increase the heatmap weight based on frequency and property magnitude
                        'heatmap-weight': 0.05,
                        // Increase the heatmap color weight weight by zoom level
                        // heatmap-intensity is a multiplier on top of heatmap-weight
                        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 0, 1, 9, 3],
                        // Color ramp for heatmap.  Domain is 0 (low) to 1 (high).
                        // Begin color ramp at 0-stop with a 0-transparancy color
                        // to create a blur-like effect.
                        'heatmap-color': [
                            'interpolate',
                            ['linear'],
                            ['heatmap-density'],
                            0,
                            'rgba(33,102,172,0)',
                            0.2,
                            'rgb(103,169,207)',
                            0.4,
                            'rgb(209,229,240)',
                            0.6,
                            'rgb(253,219,199)',
                            0.8,
                            'rgb(239,138,98)',
                            1,
                            'rgb(178,24,43)'
                        ],
                        // Adjust the heatmap radius by zoom level
                        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 0, 8, 9, 20]
                    }
                },
                'waterway-label'
            );

            setMapLoaded(true);

            map.getSource('jobs').setData(featureCollection(points.map((it) => point(it))));

            map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
            map.addControl(new mapboxgl.FullscreenControl(), 'top-left');

            fitBounds();
        });
    }, []);

    useEffect(() => {
        if (mapRef.current && mapRef.current.getSource('jobs')) {
            mapRef.current
                .getSource('jobs')
                .setData(featureCollection(points.map((it) => point(it))));

            fitBounds();
        }
    }, [points, mapLoaded]);

    if (!supported) {
        return (
            <Container
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#eee',
                    userSelect: 'none'
                }}>
                <div style={{ padding: 30, textAlign: 'center' }}>
                    Something went wrong while trying to draw the map, check that your browser is up
                    to date and refresh the page.
                </div>
            </Container>
        );
    }

    return (
        <Container>
            <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
        </Container>
    );
});
